import React, { useState } from 'react';
import { Users, Calendar, BookOpen, Home, School, TrendingUp, MessageSquare, Bus, Mail, AlertCircle, Send, User, Award, Heart, Activity, FileText, Eye, Check, X, Clock, DollarSign, CreditCard, Pill, Syringe, BarChart3 } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

// COMPREHENSIVE INTEGRATED DATA
const DEMO_DATA = {
  currentParent: {
    id: 'par_001',
    firstName: 'Sarah',
    lastName: 'Wright',
    email: 'sarah.wright.parent@email.com',
    phone: '+44 555-0101',
    children: ['stud_001', 'stud_004']
  },
  
  students: [
    {
      id: 'stud_001',
      firstName: 'Oliver',
      lastName: 'Wright',
      yearGroup: 'Year 7',
      parentId: 'par_001',
      dob: '2012-05-12',
      currentGrade: 85.2,
      attendance: 95.0,
      status: 'excelling',
      recentGrades: [
        { subject: 'English', grade: 85.2, teacher: 'Sarah Mitchell' },
        { subject: 'Mathematics', grade: 82.0, teacher: 'James Chen' },
        { subject: 'Science', grade: 88.5, teacher: 'Aisha Patel' }
      ],
      improvementRate: 5.2,
      bloodType: 'A+',
      conditions: ['Asthma'],
      allergies: [],
      medications: [
        { name: 'Albuterol Inhaler', dosage: '2 puffs as needed', frequency: 'As needed', prescriber: 'Dr. Smith', startDate: '2024-01-15' }
      ],
      vaccinations: [
        { name: 'MMR', date: '2013-05-12', dueDate: null },
        { name: 'Tdap', date: '2022-05-12', dueDate: '2032-05-12' },
        { name: 'Flu', date: '2024-10-15', dueDate: '2025-10-15' }
      ],
      emergencyContact: 'Mrs. Sarah Wright - 555-0101',
      doctorName: 'Dr. James Smith',
      doctorPhone: '555-0301',
      insuranceProvider: 'Blue Cross',
      insuranceNumber: 'BC123456789',
      lastCheckup: '2024-09-01',
      nextCheckup: '2025-03-01',
      medicalNotes: 'Asthma - inhaler in nurse office',
      hasIEP: false,
      hasMedical: true
    },
    {
      id: 'stud_004',
      firstName: 'Isla',
      lastName: 'Brown',
      yearGroup: 'Year 7',
      parentId: 'par_001',
      dob: '2012-11-07',
      currentGrade: 91.5,
      attendance: 98.0,
      status: 'excelling',
      recentGrades: [
        { subject: 'English', grade: 91.5, teacher: 'Sarah Mitchell' },
        { subject: 'Mathematics', grade: 90.0, teacher: 'James Chen' },
        { subject: 'Science', grade: 93.2, teacher: 'Aisha Patel' }
      ],
      improvementRate: 3.8,
      bloodType: 'O-',
      conditions: [],
      allergies: ['Peanuts', 'Tree Nuts'],
      medications: [
        { name: 'EpiPen', dosage: '0.3mg', frequency: 'Emergency only', prescriber: 'Dr. Johnson', startDate: '2024-01-10' }
      ],
      vaccinations: [
        { name: 'MMR', date: '2013-11-07', dueDate: null },
        { name: 'Tdap', date: '2022-11-07', dueDate: '2032-11-07' },
        { name: 'Flu', date: '2024-10-20', dueDate: '2025-10-20' }
      ],
      emergencyContact: 'Mr. David Brown - 555-0104',
      doctorName: 'Dr. Emily Johnson',
      doctorPhone: '555-0302',
      insuranceProvider: 'Aetna',
      insuranceNumber: 'AE987654321',
      lastCheckup: '2024-08-15',
      nextCheckup: '2025-02-15',
      medicalNotes: 'Peanut allergy - EpiPen required',
      hasIEP: false,
      hasMedical: true
    }
  ],

  financialAccount: {
    accountId: 'acc_001',
    totalAnnualFees: 25000,
    paidToDate: 16666,
    outstandingBalance: 8334,
    nextPaymentDue: '2025-01-15',
    nextPaymentAmount: 8334,
    paymentStatus: 'current',
    paymentHistory: [
      { id: 'pay_001', date: '2024-12-28', amount: 12500, method: 'Bank Transfer', status: 'completed', description: 'Term 2 Payment - Oliver Wright', invoiceNo: 'INV-2024-1234' },
      { id: 'pay_002', date: '2024-09-15', amount: 4166, method: 'Credit Card', status: 'completed', description: 'Term 1 Payment - Isla Brown', invoiceNo: 'INV-2024-0987' }
    ],
    scholarships: [],
    feeBreakdown: [
      { student: 'Oliver Wright', annualFee: 12500, termFee: 4166, paid: 8332, outstanding: 4168 },
      { student: 'Isla Brown', annualFee: 12500, termFee: 4166, paid: 8334, outstanding: 4166 }
    ]
  },

  assessmentFeedback: [
    {
      id: 'sub_001',
      studentId: 'stud_001',
      assessmentTitle: 'Romeo and Juliet Essay',
      className: '7A English',
      submittedAt: '2024-12-28T14:30:00',
      status: 'graded',
      totalPoints: 100,
      score: 85,
      feedback: 'Excellent analysis of the theme of fate! Your use of quotations is strong and your argument is well-structured.',
      gradedAt: '2024-12-29T16:00:00'
    },
    {
      id: 'sub_002',
      studentId: 'stud_004',
      assessmentTitle: 'Algebra Test',
      className: '7A Mathematics',
      submittedAt: '2024-12-20T10:00:00',
      status: 'graded',
      totalPoints: 50,
      score: 48,
      feedback: 'Excellent work! Minor calculation error on question 3, but overall outstanding performance.',
      gradedAt: '2024-12-21T14:00:00'
    }
  ],

  meetingRequests: [
    {
      id: 'meet_001',
      type: 'incoming',
      teacherId: 'tchr_001',
      teacherName: 'Sarah Mitchell',
      studentId: 'stud_001',
      studentName: 'Oliver Wright',
      reason: 'Academic Progress',
      preferredDate: '2025-01-08',
      preferredTime: 'afternoon',
      notes: 'Would like to discuss Oliver\'s excellent progress and potential for advanced placement',
      status: 'pending',
      submittedAt: '2024-12-28T10:00:00'
    }
  ]
};

const BriSchoolParentBootstrap = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewingFeedback, setViewingFeedback] = useState(null);
  const [viewingHealth, setViewingHealth] = useState(null);

  const [meetingRequests, setMeetingRequests] = useState(DEMO_DATA.meetingRequests);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const respondToMeeting = (requestId, response) => {
    setMeetingRequests(meetingRequests.map(req => 
      req.id === requestId 
        ? { ...req, status: response, respondedAt: new Date().toISOString() }
        : req
    ));
    alert(`Meeting ${response}!`);
  };

  const getChildById = (id) => DEMO_DATA.students.find(s => s.id === id);

  const getGradeColor = (grade) => {
    if (grade >= 85) return 'text-success';
    if (grade >= 75) return 'text-info';
    if (grade >= 65) return 'text-warning';
    return 'text-danger';
  };

  // Viewing Health Record
  if (viewingHealth) {
    const student = viewingHealth;

    return (
      <div className="min-vh-100 bg-light">
        <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
          <div className="container-fluid">
            <button onClick={() => setViewingHealth(null)} className="btn btn-link text-decoration-none">
              ← Back
            </button>
            <span className="navbar-brand mb-0 h1">
              <Heart className="me-2" size={32} style={{color: '#dc3545'}} />
              Health Record - {student.firstName} {student.lastName}
            </span>
          </div>
        </nav>

        <div className="container py-4">
          <div className="row g-4">
            <div className="col-lg-4">
              <div className="card shadow mb-3">
                <div className="card-body">
                  <h5 className="card-title">Personal Information</h5>
                  <div className="list-group list-group-flush">
                    <div className="list-group-item d-flex justify-content-between px-0">
                      <span className="text-muted">Date of Birth:</span>
                      <strong>{student.dob}</strong>
                    </div>
                    <div className="list-group-item d-flex justify-content-between px-0">
                      <span className="text-muted">Blood Type:</span>
                      <strong className="text-danger">{student.bloodType}</strong>
                    </div>
                    <div className="list-group-item d-flex justify-content-between px-0">
                      <span className="text-muted">Doctor:</span>
                      <div className="text-end">
                        <strong>{student.doctorName}</strong>
                        <div><small className="text-muted">{student.doctorPhone}</small></div>
                      </div>
                    </div>
                    <div className="list-group-item d-flex justify-content-between px-0">
                      <span className="text-muted">Insurance:</span>
                      <div className="text-end">
                        <strong>{student.insuranceProvider}</strong>
                        <div><small className="text-muted">{student.insuranceNumber}</small></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow">
                <div className="card-body">
                  <h5 className="card-title">Health Checkups</h5>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Last Checkup:</span>
                    <strong>{student.lastCheckup}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Next Checkup:</span>
                    <strong className="text-primary">{student.nextCheckup}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              {student.conditions.length > 0 && (
                <div className="card shadow mb-3">
                  <div className="card-body">
                    <h5 className="card-title">
                      <AlertCircle className="me-2" size={20} style={{color: '#dc3545'}} />
                      Medical Conditions
                    </h5>
                    <div className="row">
                      {student.conditions.map((condition, idx) => (
                        <div key={idx} className="col-md-6 mb-2">
                          <div className="alert alert-danger mb-0">{condition}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {student.allergies.length > 0 && (
                <div className="card shadow mb-3">
                  <div className="card-body">
                    <h5 className="card-title">
                      <AlertCircle className="me-2" size={20} style={{color: '#ffc107'}} />
                      Allergies
                    </h5>
                    <div className="row">
                      {student.allergies.map((allergy, idx) => (
                        <div key={idx} className="col-md-6 mb-2">
                          <div className="alert alert-warning mb-0">{allergy}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {student.medications.length > 0 && (
                <div className="card shadow mb-3">
                  <div className="card-body">
                    <h5 className="card-title">
                      <Pill className="me-2" size={20} style={{color: '#0d6efd'}} />
                      Current Medications
                    </h5>
                    {student.medications.map((med, idx) => (
                      <div key={idx} className="card bg-light mb-2">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title mb-0">{med.name}</h6>
                            <small className="text-muted">Since: {med.startDate}</small>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <small className="text-muted">Dosage:</small>
                              <p className="mb-0">{med.dosage}</p>
                            </div>
                            <div className="col-md-6">
                              <small className="text-muted">Frequency:</small>
                              <p className="mb-0">{med.frequency}</p>
                            </div>
                          </div>
                          <small className="text-muted">Prescribed by: {med.prescriber}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="card shadow">
                <div className="card-body">
                  <h5 className="card-title">
                    <Syringe className="me-2" size={20} style={{color: '#198754'}} />
                    Immunization Record
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Vaccine</th>
                          <th>Date Given</th>
                          <th>Next Due</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {student.vaccinations.map((vax, idx) => (
                          <tr key={idx}>
                            <td className="fw-bold">{vax.name}</td>
                            <td>{vax.date}</td>
                            <td>{vax.dueDate || 'Complete'}</td>
                            <td className="text-center">
                              <Check className="text-success" size={20} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Viewing Assessment Feedback
  if (viewingFeedback) {
    const child = getChildById(viewingFeedback.studentId);
    const percentage = (viewingFeedback.score / viewingFeedback.totalPoints * 100).toFixed(0);

    return (
      <div className="min-vh-100 bg-light">
        <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
          <div className="container-fluid">
            <button onClick={() => setViewingFeedback(null)} className="btn btn-link text-decoration-none">
              ← Back
            </button>
            <span className="navbar-brand mb-0 h1">
              Assessment Feedback - {child.firstName} {child.lastName}
            </span>
          </div>
        </nav>

        <div className="container py-4">
          <div className="card text-white bg-success shadow mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="mb-1 opacity-75">{child.firstName}'s Score</p>
                  <h1 className="display-3 mb-0">{viewingFeedback.score}/{viewingFeedback.totalPoints}</h1>
                  <p className="fs-4 mb-0">{percentage}%</p>
                </div>
                <Award size={80} className="opacity-25" />
              </div>
            </div>
          </div>

          <div className="card shadow mb-4">
            <div className="card-body">
              <h5 className="card-title">Assessment Details</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Class:</small>
                  <p className="fw-bold mb-0">{viewingFeedback.className}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Submitted:</small>
                  <p className="fw-bold mb-0">{new Date(viewingFeedback.submittedAt).toLocaleDateString()}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Graded:</small>
                  <p className="fw-bold mb-0">{new Date(viewingFeedback.gradedAt).toLocaleDateString()}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Grade:</small>
                  <p className={`fw-bold fs-4 mb-0 ${getGradeColor(percentage)}`}>{percentage}%</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow">
            <div className="card-body">
              <h5 className="card-title">
                <FileText className="me-2" size={20} />
                Teacher's Feedback
              </h5>
              <div className="alert alert-success">
                <p className="mb-0">{viewingFeedback.feedback}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
        <div className="container-fluid">
          <span className="navbar-brand mb-0 h1">
            <School className="me-2" size={32} style={{color: '#198754'}} />
            BriSchool Parent Portal
          </span>
          <div className="d-flex align-items-center">
            <div className="text-end me-3">
              <div className="fw-bold">{DEMO_DATA.currentParent.firstName} {DEMO_DATA.currentParent.lastName}</div>
              <small className="text-muted">{DEMO_DATA.currentParent.email}</small>
            </div>
            <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                 style={{width: '40px', height: '40px', fontWeight: 'bold'}}>
              {DEMO_DATA.currentParent.firstName[0]}{DEMO_DATA.currentParent.lastName[0]}
            </div>
          </div>
        </div>
      </nav>

      {/* Navigation */}
      <div className="bg-white border-bottom">
        <div className="container-fluid">
          <ul className="nav nav-pills py-2">
            <li className="nav-item">
              <button onClick={() => setActiveTab('dashboard')} className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}>
                <Home size={16} className="me-2" />Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('academic')} className={`nav-link ${activeTab === 'academic' ? 'active' : ''}`}>
                <BarChart3 size={16} className="me-2" />Academic Progress
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('financial')} className={`nav-link ${activeTab === 'financial' ? 'active' : ''}`}>
                <DollarSign size={16} className="me-2" />Financial Account
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('health')} className={`nav-link ${activeTab === 'health' ? 'active' : ''}`}>
                <Heart size={16} className="me-2" />Health Records
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('meetings')} className={`nav-link ${activeTab === 'meetings' ? 'active' : ''}`}>
                <MessageSquare size={16} className="me-2" />Meetings
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid py-4">
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="mb-4">My Children</h2>

            <div className="row g-4">
              {DEMO_DATA.students.map((child, idx) => {
                const avgGrade = child.recentGrades.reduce((sum, g) => sum + g.grade, 0) / child.recentGrades.length;
                
                return (
                  <div key={child.id} className="col-md-6">
                    <div className="card shadow h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="d-flex align-items-center">
                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" 
                                 style={{width: '60px', height: '60px', fontSize: '24px', fontWeight: 'bold'}}>
                              {child.firstName[0]}{child.lastName[0]}
                            </div>
                            <div>
                              <h4 className="mb-1">{child.firstName} {child.lastName}</h4>
                              <p className="text-muted mb-0">{child.yearGroup}</p>
                              {child.hasMedical && (
                                <span className="badge bg-danger mt-1">
                                  <Heart size={12} className="me-1" />Medical
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`badge ${
                            child.status === 'excelling' ? 'bg-success' :
                            child.status === 'at-risk' ? 'bg-danger' : 'bg-info'
                          }`}>
                            {child.status === 'excelling' ? 'Excelling' : child.status === 'at-risk' ? 'At Risk' : 'On Track'}
                          </span>
                        </div>

                        <div className="row g-3 mb-3">
                          <div className="col-4">
                            <div className="card bg-success bg-opacity-10 border-success">
                              <div className="card-body p-2 text-center">
                                <small className="text-muted d-block">Avg Grade</small>
                                <h5 className={`mb-0 ${getGradeColor(avgGrade)}`}>{avgGrade.toFixed(1)}%</h5>
                              </div>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="card bg-info bg-opacity-10 border-info">
                              <div className="card-body p-2 text-center">
                                <small className="text-muted d-block">Attendance</small>
                                <h5 className="text-info mb-0">{child.attendance}%</h5>
                              </div>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="card bg-primary bg-opacity-10 border-primary">
                              <div className="card-body p-2 text-center">
                                <small className="text-muted d-block">Improvement</small>
                                <h5 className="text-primary mb-0">+{child.improvementRate}%</h5>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="d-grid gap-2 d-md-flex">
                          <button onClick={() => setActiveTab('academic')} className="btn btn-success btn-sm flex-fill">
                            <BarChart3 size={14} className="me-1" />Academic
                          </button>
                          <button onClick={() => setViewingHealth(child)} className="btn btn-danger btn-sm flex-fill">
                            <Heart size={14} className="me-1" />Health
                          </button>
                          <button onClick={() => setActiveTab('financial')} className="btn btn-info btn-sm flex-fill">
                            <DollarSign size={14} className="me-1" />Fees
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'academic' && (
          <div>
            <h2 className="mb-4">Academic Progress</h2>

            {DEMO_DATA.students.map((child, idx) => {
              const avgGrade = child.recentGrades.reduce((sum, g) => sum + g.grade, 0) / child.recentGrades.length;
              const childFeedback = DEMO_DATA.assessmentFeedback.filter(f => f.studentId === child.id);
              
              return (
                <div key={child.id} className="card shadow mb-4">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="d-flex align-items-center">
                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" 
                             style={{width: '50px', height: '50px', fontWeight: 'bold'}}>
                          {child.firstName[0]}{child.lastName[0]}
                        </div>
                        <div>
                          <h4 className="mb-0">{child.firstName} {child.lastName}</h4>
                          <p className="text-muted mb-0">{child.yearGroup}</p>
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Overall Average</small>
                        <h2 className={`mb-0 ${getGradeColor(avgGrade)}`}>{avgGrade.toFixed(1)}%</h2>
                      </div>
                    </div>

                    <h5 className="mb-3">Current Grades by Subject</h5>
                    <div className="row g-3 mb-4">
                      {child.recentGrades.map((grade, i) => (
                        <div key={i} className="col-md-4">
                          <div className="card bg-light">
                            <div className="card-body">
                              <small className="text-muted d-block">{grade.subject}</small>
                              <h3 className={`mb-1 ${getGradeColor(grade.grade)}`}>{grade.grade}%</h3>
                              <small className="text-muted">Teacher: {grade.teacher}</small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {childFeedback.length > 0 && (
                      <div>
                        <h5 className="mb-3">Recent Assessment Feedback</h5>
                        {childFeedback.map(feedback => {
                          const percentage = (feedback.score / feedback.totalPoints * 100).toFixed(0);
                          return (
                            <div key={feedback.id} className="card bg-success bg-opacity-10 mb-2">
                              <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="flex-grow-1">
                                    <h6 className="mb-1">{feedback.assessmentTitle}</h6>
                                    <p className="text-muted mb-0 small">{feedback.className}</p>
                                    <p className="text-muted mb-0 small">Graded: {new Date(feedback.gradedAt).toLocaleDateString()}</p>
                                  </div>
                                  <div className="text-center me-3">
                                    <h3 className={`mb-0 ${getGradeColor(percentage)}`}>{feedback.score}/{feedback.totalPoints}</h3>
                                    <small className="text-muted">{percentage}%</small>
                                  </div>
                                  <button onClick={() => setViewingFeedback(feedback)} className="btn btn-success">
                                    <Eye size={16} className="me-2" />View
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'financial' && (
          <div>
            <h2 className="mb-4">Financial Account</h2>

            <div className="row g-4 mb-4">
              <div className="col-md-3">
                <div className="card text-white bg-primary shadow">
                  <div className="card-body">
                    <h6 className="opacity-75">Total Annual Fees</h6>
                    <h2>{formatCurrency(DEMO_DATA.financialAccount.totalAnnualFees)}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-white bg-success shadow">
                  <div className="card-body">
                    <h6 className="opacity-75">Paid to Date</h6>
                    <h2>{formatCurrency(DEMO_DATA.financialAccount.paidToDate)}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-white bg-warning shadow">
                  <div className="card-body">
                    <h6 className="opacity-75">Outstanding Balance</h6>
                    <h2>{formatCurrency(DEMO_DATA.financialAccount.outstandingBalance)}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-primary shadow">
                  <div className="card-body">
                    <h6 className="text-muted">Next Payment Due</h6>
                    <p className="fs-5 text-primary fw-bold mb-0">{DEMO_DATA.financialAccount.nextPaymentDue}</p>
                    <p className="text-primary mb-0">{formatCurrency(DEMO_DATA.financialAccount.nextPaymentAmount)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow mb-4">
              <div className="card-header">
                <h5 className="mb-0">Fee Breakdown by Student</h5>
              </div>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Student</th>
                      <th className="text-end">Annual Fee</th>
                      <th className="text-end">Paid</th>
                      <th className="text-end">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_DATA.financialAccount.feeBreakdown.map((item, idx) => (
                      <tr key={idx}>
                        <td className="fw-bold">{item.student}</td>
                        <td className="text-end text-primary fw-bold">{formatCurrency(item.annualFee)}</td>
                        <td className="text-end text-success fw-bold">{formatCurrency(item.paid)}</td>
                        <td className="text-end text-warning fw-bold">{formatCurrency(item.outstanding)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card shadow">
              <div className="card-header">
                <h5 className="mb-0">Payment History</h5>
              </div>
              <div className="card-body">
                {DEMO_DATA.financialAccount.paymentHistory.map(payment => (
                  <div key={payment.id} className="card bg-success bg-opacity-10 mb-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{payment.description}</h6>
                          <p className="text-muted mb-0 small">{payment.date} • {payment.method}</p>
                          <p className="text-muted mb-0 small">Invoice: {payment.invoiceNo}</p>
                        </div>
                        <div className="text-end">
                          <h3 className="text-success mb-0">{formatCurrency(payment.amount)}</h3>
                          <span className="badge bg-success">Completed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'health' && (
          <div>
            <h2 className="mb-4">Children's Health Records</h2>

            <div className="row g-4">
              {DEMO_DATA.students.map((child, idx) => (
                <div key={child.id} className="col-md-6">
                  <div className="card shadow h-100 border-danger">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="card-title mb-1">{child.firstName} {child.lastName}</h5>
                          <p className="text-muted mb-0">{child.yearGroup}</p>
                        </div>
                        <span className="badge bg-danger">{child.bloodType}</span>
                      </div>

                      {child.medicalNotes && (
                        <div className="alert alert-danger py-2 mb-3">
                          <AlertCircle size={16} className="me-2" />
                          <small><strong>{child.medicalNotes}</strong></small>
                        </div>
                      )}

                      {child.conditions.length > 0 && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Conditions:</small>
                          {child.conditions.map((cond, i) => (
                            <span key={i} className="badge bg-danger me-1 mb-1">{cond}</span>
                          ))}
                        </div>
                      )}

                      {child.allergies.length > 0 && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Allergies:</small>
                          {child.allergies.map((allergy, i) => (
                            <span key={i} className="badge bg-warning text-dark me-1 mb-1">{allergy}</span>
                          ))}
                        </div>
                      )}

                      {child.medications.length > 0 && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Medications:</small>
                          {child.medications.map((med, i) => (
                            <div key={i} className="bg-light rounded p-2 mb-1">
                              <small><strong>{med.name}</strong> - {med.dosage}</small>
                            </div>
                          ))}
                        </div>
                      )}

                      <button onClick={() => setViewingHealth(child)} className="btn btn-danger w-100">
                        <Heart size={16} className="me-2" />
                        View Complete Health Record
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'meetings' && (
          <div>
            <h2 className="mb-4">Meeting Requests</h2>

            {meetingRequests.filter(m => m.type === 'incoming').length > 0 ? (
              <div className="row g-4">
                {meetingRequests.filter(m => m.type === 'incoming').map(request => (
                  <div key={request.id} className="col-md-6">
                    <div className="card shadow">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="card-title">{request.teacherName}</h5>
                            <p className="text-muted mb-1">Student: {request.studentName}</p>
                            <p className="text-muted mb-0">Reason: {request.reason}</p>
                          </div>
                          <span className={`badge ${
                            request.status === 'pending' ? 'bg-warning' :
                            request.status === 'accepted' ? 'bg-success' : 'bg-danger'
                          }`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </div>

                        <div className="alert alert-light mb-3">
                          <p className="mb-1"><strong>Preferred Date:</strong> {request.preferredDate}</p>
                          <p className="mb-1"><strong>Preferred Time:</strong> {request.preferredTime.charAt(0).toUpperCase() + request.preferredTime.slice(1)}</p>
                          {request.notes && <p className="mb-0"><strong>Notes:</strong> {request.notes}</p>}
                        </div>

                        {request.status === 'pending' && (
                          <div className="d-grid gap-2">
                            <button onClick={() => respondToMeeting(request.id, 'accepted')} className="btn btn-success">
                              <Check size={16} className="me-2" />Accept
                            </button>
                            <button onClick={() => respondToMeeting(request.id, 'declined')} className="btn btn-danger">
                              <X size={16} className="me-2" />Decline
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card shadow">
                <div className="card-body text-center py-5">
                  <p className="text-muted mb-0">No incoming meeting requests</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BriSchoolParentBootstrap;